import { useCallback, useRef, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

export const useAgGrid = (fetchAction, initialCustomParams = {}) => {
  const dispatch = useDispatch();
  const gridRef = useRef();
  const [gridApi, setGridApi] = useState(null);
  const [currentParams, setCurrentParams] = useState({
    total: 0,
    sortModel: [],
    filterModel: {},
    customParams: initialCustomParams // Store custom params
  });

  const customParamsRef = useRef(currentParams.customParams);
  
  useEffect(() => {
    customParamsRef.current = currentParams.customParams;
  }, [currentParams.customParams]);

  // Server-side datasource
  const debounceTimerRef = useRef(null);
  const pendingParamsRef = useRef(null);

  const createDatasource = useCallback(() => {
    return {
      getRows: (params) => {
        // If there's an outstanding params object, tell AG Grid it's superseded
        if (pendingParamsRef.current && pendingParamsRef.current !== params) {
          try {
            pendingParamsRef.current.fail();
          } catch (e) {
            // ignore
          }
        }

        // Save the latest params
        pendingParamsRef.current = params;

        // Debounce server calls to reduce rapid repeated requests
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(async () => {
          const active = pendingParamsRef.current;
          if (!active) return;

          try {
            const { startRow, endRow, sortModel, filterModel } = active.request;

            const response = await dispatch(fetchAction({
              startRow,
              endRow,
              sortModel: sortModel || [],
              filterModel: filterModel || {},
              ...customParamsRef.current
            })).unwrap();

            // Guard against out-of-order resolution: if a newer request has
            // superseded this one while awaiting, drop this stale result so it
            // doesn't paint old rows or thrash the row count.
            if (pendingParamsRef.current !== active) return;

            const rowData = response.rows?.map(item => item.attributes || item) || [];
            const rowCount = response.lastRow || response.total || rowData.length;

            setCurrentParams(prev => ({
              ...prev,
              sortModel,
              filterModel,
              rowCount
            }));

            active.success({
              rowData,
              rowCount
            });
          } catch (error) {
            if (pendingParamsRef.current === active) active.fail();
          } finally {
            // clear pending if it was handled
            if (pendingParamsRef.current === active) {
              pendingParamsRef.current = null;
            }
            debounceTimerRef.current = null;
          }
        }, 300); // adjust debounce delay (ms) as needed
      }
    };
  }, [dispatch, fetchAction]);

  // Grid ready handler
  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
    params.api.setGridOption('serverSideDatasource', createDatasource());
    // Set getRowId callback for transaction support
    params.api.setGridOption('getRowId', (params) => params.data.id);
  }, [createDatasource]);

  // Refresh grid
  const refreshGrid = useCallback(() => {
    gridApi?.refreshServerSide({ purge: true });
  }, [gridApi]);

  // Refresh only specific row by ID
  const refreshRow = useCallback((rowId) => {
    if (!gridApi) return;
    
    const rowNode = gridApi.getRowNode(rowId);
    if (rowNode) {
      // Refresh this specific row from server
      gridApi.refreshServerSide({
        route: [rowNode],
        purge: false
      });
    }
  }, [gridApi]);

  const isRowInCache = useCallback((rowId) => {
    if (!gridApi) return false;
    
    let found = false;
    gridApi.forEachNode((node) => {
      if (node.data?.id === rowId) {
        found = true;
      }
    });
    return found;
  }, [gridApi]);

  // Update row data (optimistic update for cached rows)
  const updateRow = useCallback((updatedData) => {
    if (!gridApi) return { success: false, inCache: false };
    
    const inCache = isRowInCache(updatedData.id);
    
    if (inCache) {
      // Row is in cache - update it directly
      const result = gridApi.applyServerSideTransaction({
        update: [updatedData]
      });
      return { success: result?.update?.length > 0, inCache: true };
    }
    
    // Row not in cache - it will be updated when user scrolls to it
    return { success: false, inCache: false };
  }, [gridApi, isRowInCache]);

   // Add new row without full refresh
  const addRow = useCallback((newData) => {
    if (!gridApi) return { success: false };
    
    const result = gridApi.applyServerSideTransaction({
      add: [newData],
      addIndex: 0 // Add at top
    });
    
    return { success: result?.add?.length > 0 };
  }, [gridApi]);

  // Remove row without full refresh
  const removeRow = useCallback((rowDataId) => {
    if (!gridApi) return { success: false, inCache: false };

    // Check if row exists in cache and find it
    let targetNode = null;
    gridApi.forEachNode((node) => {
      if (node.data?.id === rowDataId) {
        targetNode = node;
      }
    });
    
    if (targetNode) {
      // Row found in cache - remove it
      const result = gridApi.applyServerSideTransaction({
        remove: [targetNode.data]
      });
      
      return { 
        success: result?.remove?.length > 0, 
        inCache: true 
      };
    }
    
    // Row not in cache
    return { success: false, inCache: false };
  }, [gridApi]);

  // Clear filters and sort
  const clearAll = useCallback(() => {
    if (gridApi) {
      gridApi.setFilterModel(null);
      const columnState = gridApi.getColumnState().map(col => ({
        ...col,
        sort: null
      }));
      gridApi.applyColumnState({ state: columnState });
      setCurrentParams(prev => ({ ...prev, sortModel: [], filterModel: {} }));
      refreshGrid();
    }
  }, [gridApi, refreshGrid]);

  // Update custom params and refresh grid
  const updateCustomParams = useCallback((newParams) => {
    // Update the ref synchronously so the (debounced) refetch reads the fresh
    // custom params rather than waiting for the post-render effect.
    const merged = { ...customParamsRef.current, ...newParams };
    customParamsRef.current = merged;

    setCurrentParams(prev => ({
      ...prev,
      customParams: merged
    }));

    // Refresh grid to fetch with new params
    if (gridApi) {
      gridApi.refreshServerSide({ purge: true });
    }
  }, [gridApi]);

  return {
    gridRef,
    gridApi,
    currentParams,
    onGridReady,
    refreshGrid,
    refreshRow,
    updateRow,
    addRow,
    removeRow,
    clearAll,
    updateCustomParams
    //exportData
  };
};